import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Stack, 
  Typography, 
  Button, 
  Box, 
  Card,
  Avatar,
  Divider,
  Fade,
  Zoom,
  Paper,
  Chip,
  Tooltip,
  Grid,
  IconButton
} from '@mui/material';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup, Tooltip as LeafletTooltip } from 'react-leaflet';

import { GetAll, apiURL } from '../../../../utils/apiCalls';
import L from 'leaflet';
import { 
  Map as MapIcon, 
  FilterList, 
  Visibility,
  Category,
  PhotoLibrary,
  Business,
  Image,
  LocationOn,
  MyLocation,
  OpenInNew
} from '@mui/icons-material';

const Map = () => {
    const [locations, setLocations] = useState([])
    async function GetList() {
        const response = await GetAll(apiURL + '/api/v1/map_project_locations?is_active=true');
        if (response) {
            if (response.data.status) {
                setLocations(response.data.data)
            }
            else {
                toast.error(response.data.message)
                setLocations({})
            }
        }
        else {
        }
    };
    useEffect(() => {
        GetList()
    }, []);

    const telanganaBounds = [
        [16.0, 77.0],
        [20.0, 81.0] 
    ];

    const handleOpenGoogleMaps = (lat, lag) => {
        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lag}`;
        window.open(googleMapsUrl, '_blank');
    };


    return (
        <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
            {/* Professional Header Section */}
    <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
                <Avatar sx={{ 
                    bgcolor: '#1976d2', 
                    width: 48, 
                    height: 48,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                }}>
                    <MapIcon sx={{ fontSize: 24, color: 'white' }} />
                </Avatar>
                <Box>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary">
                        Project Locations Map
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        Interactive map showing all project locations across Telangana
                    </Typography>
                </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
        </Box>
    </Fade>

            {/* Main Content */}
            <Zoom in timeout={1000}>
                <Card elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ p: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <FilterList sx={{ color: '#1976d2', fontSize: 28 }} />
                            <Typography variant="h6" fontWeight="600" color="text.primary">
                                Interactive Map
                            </Typography>
                            <Chip
                                label={`${locations?.length || 0} Locations`}
                                sx={{
                                    background: '#1976d2',
                                    color: 'white',
                                    fontWeight: 600,
                                    ml: 'auto'
                                }}
                            />
                        </Stack>
                    </Box>

                    <Box sx={{ position: 'relative' }}>
                        <MapContainer
                            center={[17.385044, 78.486671]}
                            zoom={7}
                            style={{ width: '100%', height: '600px' }}
                            bounds={telanganaBounds}
                            maxBounds={telanganaBounds}
                            maxBoundsViscosity={1.0}
                            zoomDelta={0.15}
                            zoomSnap={0.15}
                            scrollWheelZoom={{
                                wheelDebounceTime: 100,
                                wheelPxPerZoomLevel: 1000
                            }}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution=""
                            />
                            {locations.map((location, index) => (
                                <Marker
                                    key={index}
                                    position={[location.lat, location.lag]}
                                    icon={new L.Icon({
                                        iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
                                        iconSize: [30, 30],
                                        iconAnchor: [15, 15],
                                    })}
                                >
                                    <LeafletTooltip direction="right" offset={[0, 0]} permanent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOn sx={{ color: '#1976d2', fontSize: 16 }} />
                                            <Typography variant="body2" fontWeight="600">
                                                {location.name}
                                            </Typography>
                                        </Box>
                                    </LeafletTooltip>
                                    <Popup>
                                        <Box sx={{ p: 1, minWidth: 200 }}>
                                            <Stack spacing={2}>
                                                <Box>
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                        <Business sx={{ color: '#1976d2', fontSize: 20 }} />
                                                        <Typography variant="h6" fontWeight="600" color="#1976d2">
                                                            {location.name}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {location.description}
                                                    </Typography>
                                                </Box>
                                                <Divider />
                                                <Button
                                                    variant="contained"
                                                    startIcon={<OpenInNew />}
                                                    onClick={() => handleOpenGoogleMaps(location.lat, location.lag)}
                                                    sx={{
                                                        background: '#1976d2',
                                                        '&:hover': {
                                                            background: '#1565c0'
                                                        },
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    Open in Google Maps
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </Box>
                </Card>
            </Zoom>

            {/* Professional Stats Footer */}
            <Fade in timeout={1400}>
                <Paper
                    elevation={2}
                    sx={{
                        mt: 4,
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                    }}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ background: '#1976d2' }}>
                                    <MapIcon sx={{ color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="600" color="text.primary">
                                        Total Locations
                                    </Typography>
                                    <Typography variant="h4" fontWeight="700" color="#1976d2">
                                        {locations?.length || 0}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ background: '#4caf50' }}>
                                    <MyLocation sx={{ color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="600" color="text.primary">
                                        Active Projects
                                    </Typography>
                                    <Typography variant="h4" fontWeight="700" color="#4caf50">
                                        {locations?.length || 0}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ background: '#ff9800' }}>
                                    <LocationOn sx={{ color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="600" color="text.primary">
                                        Map Coverage
                                    </Typography>
                                    <Typography variant="h4" fontWeight="700" color="#ff9800">
                                        Telangana
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            </Fade>
        </Container>
    )
}
export default Map;
